import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import type { GlobalBannerProps } from './GlobalBanner.types';

import { track } from 'apis/amethyst';
import { evGlobalDualBannerClick, evGlobalDualBannerImpression } from 'events/headerFooter';
import { trackEvent } from 'helpers/analytics';
import useEffectOnce from 'hooks/useEffectOnce';
import { cn } from 'helpers/classnames';
import ConditionalLink from 'components/common/ConditionalLink';
import { fireAmplitudeEvent } from 'utils/amplitude';
import { buildAmplitudeEvent } from 'helpers/amplitudeEventBuilders';
import { useGlobalBannerAmplitudeImpression } from 'hooks/useGlobalBannerAmplitudeImpression';
import type { AppState } from 'types/app';

const GlobalBanner = ({ banner, slot, testId, pageType, sessionId }: GlobalBannerProps) => {
  const isRecognizedCustomer = useSelector((state: AppState) => state.customer);
  const directedID = useSelector((state: AppState) => state.holmes?.directedId ?? undefined);
  const isEnrolledInVip = useSelector((state: AppState) => state.rewards?.rewardsInfo?.enrolled);

  // Track impression event
  useGlobalBannerAmplitudeImpression({
    pageType,
    sessionId,
    componentName: banner.componentName,
    slotName: slot,
    creativeId: banner.creativeID
  });

  useEffectOnce(() => {
    track(() => [
      evGlobalDualBannerImpression,
      {
        creativeId: banner.creativeID,
        slot
      }
    ]);
  });

  const handleBannerClick = useCallback(() => {
    const zfcEventName = slot === 'banner-1' ? 'TE_GLOBAL_DUAL_BANNER_1_CLICK' : 'TE_GLOBAL_DUAL_BANNER_2_CLICK';

    trackEvent(zfcEventName, banner.creativeID);
    track(() => [
      evGlobalDualBannerClick,
      {
        slot,
        creativeId: banner.creativeID
      }
    ]);

    // Amplitude tracking - click event
    const clickEvent = buildAmplitudeEvent({
      event: 'click',
      pageType,
      sessionId,
      componentName: banner.componentName,
      componentType: 'global banner',
      componentContext: 'Header',
      isRecognizedCustomer,
      isEnrolledInVip: isEnrolledInVip ?? false,
      directedID,
      trackingPayload: {
        slotName: slot,
        creativeId: banner.creativeID
      }
    });

    fireAmplitudeEvent(clickEvent);
  }, [banner.creativeID, banner.componentName, slot, pageType, sessionId, isRecognizedCustomer, isEnrolledInVip, directedID]);

  // Get dimensions from the desktop source (fallback image)
  const desktopSource = banner.media.primary.sources.find(s => !s.media || s.media === '');
  const width = desktopSource?.width;
  const height = desktopSource?.height;

  const containerBreak = banner.containerBreak ?? false;

  return (
    <ConditionalLink
      href={banner.links[0]?.href}
      onClick={handleBannerClick}
      className={cn('block w-full', {
        'mx-auto max-w-layoutMaxWidth': !containerBreak,
        'max-w-full': containerBreak
      })}
      ariaLabel={banner.media.primary.alt ?? `Global Banner ${slot}`}
      testId={testId ?? 'globalBanner'}
    >
      <picture>
        {banner.media.primary.sources.map((source, index) => (
          <source key={index} {...(source.media && { media: source.media })} srcSet={source.srcset} />
        ))}
        <img src={banner.media.primary.src} alt={banner.media.primary.alt} width={width} height={height} className="block" />
      </picture>
    </ConditionalLink>
  );
};

export default GlobalBanner;
